'use client';

import { Children, Fragment } from 'react';
import type { PropsWithChildren } from 'react';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { useCarousel, useCarouselDispatch } from './useCarousel';
import { useListVirtualization } from './useListVirtualization';

export type CarouselVirtualizedListProps = PropsWithChildren<{
  /**
   * Number of items to render initially before virtualization kicks in
   * This value should be equal to or greater than the number of visible items in the carousel,
   * it can improve the user experience by reducing the chance of seeing empty spaces when scrolling fast
   * but it also increases the initial render time
   * @default 3
   */
  initialRenderCount?: number;
  /**
   * Additional class name for the carousel track
   * To override visible items or gap, use CSS variables
   * @example className="[--items-visible:1.5] @xl:[--items-visible:3.5] [--reco-gap:0.5rem]"
   */
  className?: string;
}>;

export const CarouselVirtualizedList = ({
  children,
  className,
  initialRenderCount = 3,
}: CarouselVirtualizedListProps) => {
  const { trackRef } = useCarousel();
  const { updateEdgeState } = useCarouselDispatch({ trackRef });
  const { visibleIndexes } = useListVirtualization({ trackRef, initialRenderCount });

  const items = Children.toArray(children);

  return (
    <ul
      ref={trackRef}
      className={cn(
        'grid snap-x snap-mandatory [scrollbar-width:none] auto-cols-[calc((100%-(var(--items-visible)-1)*var(--reco-gap))/var(--items-visible))] grid-flow-col gap-x-[var(--reco-gap)] overflow-x-auto overscroll-x-contain [--items-visible:1] [--reco-gap:0.375rem] [-webkit-overflow-scrolling:touch] [&>li]:animate-[fade_0.5s_ease-in_forwards] [&>li]:snap-start',
        className,
      )}
      onScroll={updateEdgeState}
    >
      {items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {visibleIndexes.has(index) ? (
            item
          ) : (
            <li className="row-start-1 -row-end-1 grid-cols-1" data-index={index} />
          )}
        </Fragment>
      ))}
    </ul>
  );
};
